import React, { useState } from 'react';
import { Card, CardFooter, CardBody, Form, Modal, ModalHeader, ModalBody } from 'reactstrap';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { Link, useHistory, useParams } from 'react-router-dom';
import { Button, Field, FormAlerts } from '@cordata/buffet';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { startOfDay } from 'date-fns';

import { authorizedInstance as axios } from '~/utils';

const validationSchema = Yup.object().shape({
  note: Yup.string().required(),
});

const initialValues = ({
  note_type: 'Patient Note',
  note: '',
});

const NotesForm = ({ data, disabled }) => {
  const history = useHistory();
  const params = useParams();
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);

  const toggleDeactivateModal = () => { setShowDeactivateModal(!showDeactivateModal); };

  const submitForm = (values, { setErrors, setSubmitting }) => {
    const p = {
      data: {
        attributes: {
          ...values,
          activity_date: startOfDay(new Date()),
        },
        relationships: {
          patient: {
            data: {
              id: params.patient_id,
            },
          },
        },
      },
    };

    const method = params.note_id ? 'delete' : 'post';
    const url = `notes${params.note_id ? `/${params.note_id}` : ''}`;

    axios[method](url, p)
      .then(() => history.push(`/patients/${params.patient_id}/face-sheet`))
      .catch(e => setErrors({ server: e.errors }))
      .then(() => setSubmitting(false));
  };

  return (
    <>
      <Formik
        displayName="NotesForm"
        validateOnBlur={false}
        validateOnChange={false}

        initialValues={{
          ...initialValues,
          ...data,
        }}

        validationSchema={validationSchema}

        onSubmit={submitForm}
      >
        {({
          handleSubmit,
          isSubmitting,
          submitCount,
          errors,
        }) => (
          <Card>
            <Form onSubmit={handleSubmit} noValidate>
              <CardBody>
                { submitCount >= 1 && errors && <FormAlerts errors={errors} /> }

                <Field
                  name="note"
                  type="textarea"
                  disabled={disabled}
                  required
                />
              </CardBody>

              <CardFooter className="d-flex">
                <div className="ml-auto">
                  <Button
                    type="cancel"
                    disabled={isSubmitting}
                    tag={Link}
                    to={`/patients/${params.patient_id}/face-sheet`}
                  >
                    Cancel
                  </Button>

                  {disabled && (
                    <Button disabled={isSubmitting} color="danger" onClick={toggleDeactivateModal}>
                      <FontAwesomeIcon icon="trash" />
                      <span className="d-none d-md-inline ml-2">{isSubmitting ? 'Deactivating ...' : 'Deactivate'}</span>
                    </Button>
                  )}

                  {!disabled && (
                    <Button type="submit" disabled={isSubmitting}>
                      {isSubmitting ? 'Working ...' : 'Save'}
                    </Button>
                  )}
                </div>
              </CardFooter>
            </Form>
            <Modal isOpen={showDeactivateModal} toggle={toggleDeactivateModal}>
              <ModalHeader toggle={toggleDeactivateModal}>Deactivate Note?</ModalHeader>
              <ModalBody className="p-0">
                <Card>
                  <CardBody>
                    <p>Are you sure you want to deactivate this note?</p>
                  </CardBody>
                  <CardFooter className="d-flex">
                    <div className="mr-auto">
                      <Button
                        type="cancel"
                        color="danger"
                        outline
                        icon="times"
                        onClick={toggleDeactivateModal}
                      >
                        Cancel
                      </Button>
                    </div>
                    <div className="ml-auto">
                      <Button
                        type="link"
                        onClick={handleSubmit}
                      >
                        Deactivate
                      </Button>
                    </div>
                  </CardFooter>
                </Card>
              </ModalBody>
            </Modal>
          </Card>
        )}
      </Formik>
    </>
  );
};

NotesForm.propTypes = {
  data: PropTypes.shape().isRequired,
  disabled: PropTypes.bool,
};

NotesForm.defaultProps = {
  disabled: false,
};

export default NotesForm;
